import React, { useState, useEffect } from 'react';
import { IconAdjustments, IconDeviceFloppy } from '@tabler/icons-react';
import api from '../api/api';

export default function SystemConfigPage({ me, showToast }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [activeGroup, setActiveGroup] = useState(null);

  const isMaster = me?.role === 'MASTER';

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const res = await api.get('/system-config/all');
      setConfigs(res.data);
      // 초기값 세팅
      const values = {};
      res.data.forEach(c => { values[c.configKey] = c.configValue || ''; });
      setEditValues(values);
      // 첫 번째 그룹 활성화
      if (res.data.length > 0 && !activeGroup) {
        const groups = [...new Set(res.data.map(c => c.configGroup))];
        setActiveGroup(groups[0]);
      }
    } catch (err) {
      showToast('설정을 불러오는데 실패했습니다.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setEditValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const changed = configs
        .filter(c => editValues[c.configKey] !== (c.configValue || ''))
        .map(c => ({ configKey: c.configKey, configValue: editValues[c.configKey] }));

      if (changed.length === 0) {
        showToast('변경된 항목이 없습니다.', 'warning');
        return;
      }

      await api.put('/system-config', changed);
      showToast('설정이 저장되었습니다.', 'success');
      await loadConfigs();
    } catch (err) {
      showToast('저장에 실패했습니다.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  // 그룹별 분류
  const groups = [...new Set(configs.map(c => c.configGroup))];
  const groupConfigs = configs.filter(c => c.configGroup === activeGroup);

  if (loading) {
    return (
      <div className="container-xl">
        <div className="page-header d-print-none mb-4">
          <div className="d-flex align-items-center gap-2">
            <IconAdjustments size={24} />
            <h2 className="page-title mb-0">사이트 설정</h2>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary" />
            <p className="text-muted mt-2">설정을 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xl">
      <div className="page-header d-print-none mb-4">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <IconAdjustments size={24} />
            <h2 className="page-title mb-0">사이트 설정</h2>
          </div>
          {isMaster && (
            <button className="btn btn-sm btn-primary d-flex align-items-center gap-1"
              onClick={handleSave} disabled={saving}>
              <IconDeviceFloppy size={16} />
              {saving ? '저장 중...' : '저장'}
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="row g-0">
          {/* 왼쪽 그룹 네비게이션 */}
          <div className="col-12 col-md-3 d-flex flex-column border-end">
            <div className="card-body p-0">
              <div className="list-group list-group-flush">
                {groups.map(group => (
                  <button key={group}
                    className={`list-group-item list-group-item-action d-flex align-items-center${activeGroup === group ? ' active' : ''}`}
                    onClick={() => setActiveGroup(group)}>
                    {group}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 오른쪽 설정 폼 */}
          <div className="col-12 col-md-9">
            <div className="card-body">
              <h3 className="card-title mb-4">{activeGroup}</h3>

              {groupConfigs.map(config => (
                <div key={config.configKey} className="mb-3">
                  <label className="form-label">
                    {config.description}
                    <small className="text-muted ms-2">({config.configKey})</small>
                  </label>

                  {config.configType === 'BOOLEAN' ? (
                    <div>
                      <label className="form-check form-switch">
                        <input type="checkbox" className="form-check-input"
                          checked={editValues[config.configKey] === 'true'}
                          disabled={!isMaster}
                          onChange={e => handleChange(config.configKey, e.target.checked ? 'true' : 'false')} />
                        <span className="form-check-label">
                          {editValues[config.configKey] === 'true' ? '활성' : '비활성'}
                        </span>
                      </label>
                    </div>
                  ) : config.configType === 'TEXTAREA' ? (
                    <textarea className="form-control form-control-sm" rows={3}
                      value={editValues[config.configKey] || ''}
                      disabled={!isMaster}
                      onChange={e => handleChange(config.configKey, e.target.value)} />
                  ) : config.configType === 'SELECT' && config.configKey === 'site.loginColor' ? (
                    <div className="d-flex gap-2 flex-wrap mt-1">
                      {[
                        { value: 'blue',   label: '블루',   c1: '#206bc4', c2: '#4263eb' },
                        { value: 'green',  label: '그린',   c1: '#2d6a4f', c2: '#52b788' },
                        { value: 'red',    label: '레드',   c1: '#8b2635', c2: '#c0392b' },
                        { value: 'purple', label: '퍼플',   c1: '#4a2c8a', c2: '#6741d9' },
                        { value: 'black',  label: '블랙',   c1: '#212529', c2: '#495057' },
                        { value: 'gray',   label: '그레이', c1: '#868e96', c2: '#adb5bd' },
                      ].map(opt => {
                        const selected = (editValues[config.configKey] || 'blue') === opt.value;
                        return (
                          <button key={opt.value} type="button" disabled={!isMaster}
                            onClick={() => handleChange(config.configKey, opt.value)}
                            style={{
                              width: 72, height: 52, borderRadius: 10, border: 'none',
                              background: `linear-gradient(135deg, ${opt.c1}, ${opt.c2})`,
                              cursor: isMaster ? 'pointer' : 'default',
                              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end',
                              paddingBottom: 6,
                              boxShadow: selected ? `0 0 0 3px #fff, 0 0 0 5px ${opt.c1}` : '0 2px 6px rgba(0,0,0,0.18)',
                              transform: selected ? 'scale(1.08)' : 'scale(1)',
                              transition: 'all 0.15s',
                            }}>
                            <span style={{ color: '#fff', fontSize: 11, fontWeight: 600, textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>{opt.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : config.configType === 'IMAGE' ? (
                    <div>
                      <div className="d-flex gap-2 align-items-center">
                        <input type="text" className="form-control form-control-sm"
                          placeholder="이미지 URL을 입력하세요"
                          value={editValues[config.configKey] || ''}
                          disabled={!isMaster}
                          onChange={e => handleChange(config.configKey, e.target.value)} />
                        {isMaster && (
                          <label className="btn btn-outline-primary btn-sm flex-shrink-0" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            파일 업로드
                            <input type="file" accept="image/*,.ico" style={{ display: 'none' }}
                              onChange={e => {
                                const file = e.target.files[0];
                                if (!file) return;
                                if (file.size > 1024 * 1024) { showToast('1MB 이하 파일만 업로드 가능합니다.', 'error'); return; }
                                const reader = new FileReader();
                                reader.onload = () => handleChange(config.configKey, reader.result);
                                reader.readAsDataURL(file);
                                e.target.value = '';
                              }} />
                          </label>
                        )}
                      </div>
                      {editValues[config.configKey] && (
                        <div className="mt-2 d-flex align-items-center gap-2">
                          <img src={editValues[config.configKey]} alt="미리보기"
                            style={{ maxHeight: 60, maxWidth: 200, objectFit: 'contain', border: '1px solid #e6e7e9', borderRadius: 4, padding: 4 }}
                            onError={e => { e.target.style.display = 'none'; }} />
                          {isMaster && (
                            <button className="btn btn-sm btn-outline-danger"
                              onClick={() => handleChange(config.configKey, '')}>삭제</button>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <input type="text" className="form-control form-control-sm"
                      value={editValues[config.configKey] || ''}
                      disabled={!isMaster}
                      onChange={e => handleChange(config.configKey, e.target.value)} />
                  )}

                  {config.updatedAt && (
                    <small className="text-muted">
                      최종 수정: {new Date(config.updatedAt).toLocaleString('ko-KR')}
                      {config.updatedBy && ` (${config.updatedBy})`}
                    </small>
                  )}
                </div>
              ))}

              {!isMaster && (
                <div className="alert alert-info mt-3">
                  MASTER 권한이 있어야 설정을 변경할 수 있습니다.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
